import { NavLink, useLocation } from "react-router";
import {
  LayoutDashboard,
  Search,
  TrendingUp,
  Lightbulb,
  FileText,
  Globe,
  MessageSquare,
  Settings,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  Terminal,
} from "lucide-react";
import { useCallback, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { getAuthToken } from "@/lib/api";

const CONSOLE_URL = "https://console.rankdisco.com";

interface NavChild {
  label: string;
  path: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { label: "Overview", path: "/", icon: LayoutDashboard },
  { label: "Research", path: "/research", icon: Search, children: [
    { label: "Keywords", path: "/research/keywords" },
    { label: "Prompts", path: "/research/prompts" },
    { label: "Apps", path: "/research/apps" },
    { label: "Competitors", path: "/research/competitors" },
    { label: "Local", path: "/research/local" },
  ]},
  { label: "Tracking", path: "/tracking", icon: TrendingUp, children: [
    { label: "Keywords", path: "/tracking/keywords" },
    { label: "Apps", path: "/tracking/apps" },
    { label: "Competitors", path: "/tracking/competitors" },
    { label: "Local", path: "/tracking/local" },
  ]},
  { label: "Insights", path: "/insights", icon: Lightbulb, children: [
    { label: "Alerts", path: "/insights/alerts" },
    { label: "Opportunities", path: "/insights/opportunities" },
    { label: "Anomalies", path: "/insights/anomalies" },
    { label: "Recommendations", path: "/insights/recommendations" },
  ]},
  { label: "Reports", path: "/reports", icon: FileText },
  { label: "Assets", path: "/assets", icon: Globe },
  { label: "Chat", path: "/chat", icon: MessageSquare },
  { label: "Settings", path: "/settings", icon: Settings, children: [
    { label: "Workspace", path: "/settings/workspace" },
    { label: "Account", path: "/settings/account" },
    { label: "Integrations", path: "/settings/integrations" },
  ]},
];

export function AppSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { logout, workspace } = useAuth();

  const openConsole = useCallback(() => {
    const token = getAuthToken();
    const url = token ? `${CONSOLE_URL}/?auth_token=${token}` : CONSOLE_URL;
    window.open(url, "_blank", "noopener");
  }, []);

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        "flex flex-col border-r border-border-1 bg-surface-1 transition-all",
        collapsed ? "w-[72px]" : "w-[264px]"
      )}
      style={{ transitionDuration: "var(--transition-duration-base)" }}
    >
      {/* Logo area */}
      <div className="flex h-16 items-center px-4 border-b border-border-1">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-white font-semibold text-sm">
            R
          </div>
          {!collapsed && (
            <span className="text-text-1 font-semibold text-sm truncate">
              RankDisco
            </span>
          )}
        </div>
      </div>

      {/* Navigation items */}
      <div className="flex-1 overflow-y-auto py-3 px-3">
        <ul className="flex flex-col gap-0.5">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);

            return (
              <li key={item.path}>
                <NavLink
                  to={item.children ? item.children[0].path : item.path}
                  className={cn(
                    "flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-sm transition-colors focus-ring",
                    isActive
                      ? "bg-primary-surface text-primary font-medium"
                      : "text-text-2 hover:bg-overlay-hover hover:text-text-1"
                  )}
                  style={{ transitionDuration: "var(--transition-duration-fast)" }}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon
                    className="w-5 h-5 shrink-0"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
                {isActive && !collapsed && item.children && (
                  <ul className="ml-8 mt-0.5 mb-1 flex flex-col gap-0.5">
                    {item.children.map((child) => {
                      const isChildActive =
                        location.pathname === child.path ||
                        location.pathname.startsWith(child.path + "/");
                      return (
                        <li key={child.path}>
                          <NavLink
                            to={child.path}
                            className={cn(
                              "block rounded-[var(--radius-sm)] px-3 py-1.5 text-[13px] transition-colors",
                              isChildActive
                                ? "text-primary font-medium bg-primary-surface"
                                : "text-text-2 hover:text-text-1 hover:bg-overlay-hover"
                            )}
                            style={{ transitionDuration: "var(--transition-duration-fast)" }}
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Bottom actions */}
      <div className="border-t border-border-1 p-3 flex flex-col gap-1">
        {(workspace?.role === "owner" || workspace?.role === "admin") && (
          <button
            onClick={openConsole}
            className="flex items-center gap-3 w-full rounded-[var(--radius-sm)] px-3 py-2 text-text-3 hover:bg-overlay-hover hover:text-text-1 transition-colors focus-ring"
            style={{ transitionDuration: "var(--transition-duration-fast)" }}
            title={collapsed ? "Console" : undefined}
          >
            <Terminal className="w-5 h-5 shrink-0" strokeWidth={1.75} />
            {!collapsed && <span className="text-sm">Console</span>}
          </button>
        )}
        <button
          onClick={logout}
          className="flex items-center gap-3 w-full rounded-[var(--radius-sm)] px-3 py-2 text-text-3 hover:bg-danger-surface hover:text-danger transition-colors focus-ring"
          style={{ transitionDuration: "var(--transition-duration-fast)" }}
          title={collapsed ? "Log out" : undefined}
        >
          <LogOut className="w-5 h-5 shrink-0" strokeWidth={1.75} />
          {!collapsed && <span className="text-sm">Log out</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center w-full rounded-[var(--radius-sm)] px-3 py-2 text-text-3 hover:bg-overlay-hover hover:text-text-2 transition-colors focus-ring"
          style={{ transitionDuration: "var(--transition-duration-fast)" }}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeft className="w-5 h-5" strokeWidth={1.75} />
          ) : (
            <>
              <PanelLeftClose className="w-5 h-5 mr-3" strokeWidth={1.75} />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </nav>
  );
}
